import React from 'react'
import { CtxType, HocType } from './types/common'

export type RedirectConditionType = (
  ctx: CtxType,
  props: any
) => undefined | false | string | { path: string; code?: 301 | 302 }

export type withPreRedirectType = (
  redirectCondition: RedirectConditionType
) => HocType<{}>

/**
 * 为页面附加前置的重定向功能
 * 功能上为渲染页面前，确认是否需重定向，需重定向时不再继续渲染
 * 类似于路由的前置钩子，路由钩子实现后替换此代码
 *
 * @param redirectCondition 传入的判断回调函数，在 server/client 的 constructor 时调用
 *                          返回的 path，将原样跳转，不处理 basePath 行为
 *
 *
 * @example
 *
 *  // 装饰器
 *  @withPreRedirect((ctx, props) => '/home') class extends React.Component {}
 *
 *  // HOC
 *  withPreRedirect((ctx, props) => '/home')( class extends React.Component {} )
 */
export const withPreRedirect: withPreRedirectType = (function withPreRedirect(
  redirectCondition: RedirectConditionType
) {
  return function wrap(Component: React.ComponentType) {
    return class PreRedirect extends React.PureComponent {
      static displayName = `withPreRedirect(${
        Component.displayName || Component.name || 'Component'
      })`

      static getInitialProps() {
        if ((Component as any).getInitialProps) {
          return (Component as any).getInitialProps.apply(this, arguments)
        }
        return {}
      }

      isRedirect = false

      constructor(props: any) {
        super(props)
        // 仅支持同步，涉及接口调用的应在 getInitialProps 或 prefetchApi 上发出，从 props 获取
        const redirectResult = redirectCondition(props.ctx, props)
        if (redirectResult) {
          // 参数格式转换，及合并默认值
          const param = {
            ...{ code: 302 },
            ...(typeof redirectResult === 'string'
              ? { path: redirectResult }
              : redirectResult),
          } as { path: string; code: 301 | 302 }

          // 不处理根路由，完全按业务提供的 path 原样跳转，业务需自行补全 basePath
          // 以便在需要时，跳转到同域名下的其他 routerBase
          if (process.server) {
            props.ctx.res.redirect(param.code, param.path)
          } else {
            // 与 301/302 行为一致，总是为 replace
            window.location.replace(param.path)
          }
          this.isRedirect = true
        }
      }

      render() {
        if (this.isRedirect) {
          return null
        } else {
          return <Component {...this.props} />
        }
      }
    }
  }
} as any) as withPreRedirectType
